import { webPaths } from "@zplanem/web-paths"
import { useEffect, useMemo, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { PropertyChat } from "../../components/chat/property-chat"
import { ApiError } from "../../lib/api-client"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { CreateTicketModal } from "../components/tickets/create-ticket-modal"
import { TicketBoard } from "../components/tickets/ticket-board"
import { TicketFilterBar } from "../components/tickets/ticket-filter-bar"
import {
	clearMissingTicketFilters,
	type TicketBoardFilters,
} from "../components/tickets/ticket-filter-model"
import { isTicketManager } from "../components/tickets/ticket-form-model"
import { TicketList } from "../components/tickets/ticket-list"
import {
	TicketAddButton,
	TicketViewSwitcher,
} from "../components/tickets/ticket-view-controls"
import { useIsDesktop } from "../hooks/use-is-desktop"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProjects } from "../hooks/use-projects"
import { useProperty } from "../hooks/use-property"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { useTasksViewMode } from "../hooks/use-tasks-view-mode"
import { useTopics } from "../hooks/use-topics"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"

export function PropertyTicketsPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const memberships = usePropertyMemberships(property?.id)
	const projects = useProjects(property?.id)
	const topics = useTopics(property?.id)
	const { setLastPropertyId } = useLastPropertyId()
	const { profile } = useUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [shareOpen, setShareOpen] = useState(false)
	const [createOpen, setCreateOpen] = useState(false)
	const [filters, setFilters] = useState<TicketBoardFilters>({})
	const isDesktop = useIsDesktop()
	const { viewMode, setViewMode } = useTasksViewMode(
		`property:${property?.id ?? "none"}`,
	)

	const isNotFound = !id || (error instanceof ApiError && error.status === 404)

	useEffect(() => {
		if (property) setLastPropertyId(property.id)
		else if (isNotFound) setLastPropertyId(null)
	}, [property, isNotFound, setLastPropertyId])

	const filterOptionIds = useMemo(
		() => ({
			projectIds: new Set((projects.data ?? []).map((project) => project.id)),
			assigneeIds: new Set([
				...(memberships.data?.owner ? [memberships.data.owner.id] : []),
				...(memberships.data?.memberships.map(
					(membership) => membership.user.id,
				) ?? []),
			]),
			topicIds: new Set((topics.data ?? []).map((topic) => topic.id)),
		}),
		[projects.data, memberships.data, topics.data],
	)

	useEffect(() => {
		setFilters((current) => {
			const next = clearMissingTicketFilters(current, filterOptionIds)
			if (
				next.projectId === current.projectId &&
				next.assigneeId === current.assigneeId &&
				next.topicId === current.topicId &&
				next.kind === current.kind
			) {
				return current
			}
			return next
		})
	}, [filterOptionIds])

	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (error) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-destructive px-6 text-center">
				{error.message}
			</div>
		)
	}
	if (!property) return null
	const canManageTickets = isTicketManager(memberships.data, profile?.id)
	// Mobile is List-only; the stored Kanban|List choice is a desktop preference.
	const effectiveMode = isDesktop ? viewMode : "list"

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer>
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<PageHeader
					eyebrow={property.name}
					eyebrowHref={webPaths.property(property.id)}
					title={t.properties.tabs.tickets}
					meta={
						<>
							<TicketViewSwitcher
								value={viewMode}
								onChange={setViewMode}
								t={t.tickets}
							/>
							{canManageTickets ? (
								<TicketAddButton
									onClick={() => setCreateOpen(true)}
									t={t.tickets}
								/>
							) : null}
						</>
					}
				/>
				<TicketFilterBar
					propertyId={property.id}
					filters={filters}
					projects={projects.data ?? []}
					memberships={memberships.data}
					topics={topics.data ?? []}
					canManageTopics={canManageTickets}
					t={t.tickets}
					onChange={setFilters}
				/>
				{effectiveMode === "kanban" ? (
					<TicketBoard
						propertyId={property.id}
						filters={filters}
						topics={topics.data ?? []}
						projects={projects.data ?? []}
						returnTo={{
							path: webPaths.propertyTickets(property.id),
							label: t.tickets.backToList,
						}}
					/>
				) : (
					<TicketList
						propertyId={property.id}
						filters={filters}
						topics={topics.data ?? []}
						projects={projects.data ?? []}
						returnTo={{
							path: webPaths.propertyTickets(property.id),
							label: t.tickets.backToList,
						}}
					/>
				)}
			</PageContainer>
			<PropertyBottomNav propertyId={property.id} />
			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			{createOpen && canManageTickets ? (
				<CreateTicketModal
					propertyId={property.id}
					initialFilters={filters}
					onClose={() => setCreateOpen(false)}
				/>
			) : null}
			<PropertyChat propertyId={property.id} />
		</div>
	)
}
